<template>
	<div class="qrscan-panel">
		<div class="d-flex flex-wrap align-center">
			<template v-if="attiva">
				<v-chip small :color="collegato ? 'success' : 'grey'" text-color="white" class="mr-2">
					<v-icon left small>mdi-cellphone</v-icon>
					{{ collegato ? 'Telefono collegato' : 'In attesa del telefono' }}
				</v-chip>
				<v-btn text small color="primary" @click="dialog = true">
					<v-icon left small>mdi-qrcode</v-icon>
					Mostra QR
				</v-btn>
				<v-btn text small color="error" @click="disconnetti">
					<v-icon left small>mdi-link-off</v-icon>
					Disconnetti
				</v-btn>
			</template>
			<v-btn v-else outlined small color="primary" :loading="loading" @click="genera">
				<v-icon left small>mdi-cellphone-link</v-icon>
				Leggi con il telefono
			</v-btn>
			<span v-if="errore" class="caption error--text ml-3">{{ errore }}</span>
			<span v-else-if="scaduta" class="caption grey--text text--darken-1 ml-3">Sessione telefono scaduta: rigenera il QR code.</span>
		</div>

		<v-dialog v-model="dialog" max-width="460">
			<v-card>
				<v-card-title class="text-h6">Leggi i codici con il telefono</v-card-title>
				<v-card-text>
					<div class="d-flex justify-center mb-4">
						<div class="qr-box">
							<v-img v-if="qr" :src="qr" width="220" height="220" contain />
							<v-progress-circular v-else indeterminate color="primary" size="48" />
						</div>
					</div>
					<div class="mb-2">
						<v-chip small :color="collegato ? 'success' : 'grey'" text-color="white">{{ collegato ? 'Telefono collegato' : 'In attesa di connessione' }}</v-chip>
						<span v-if="scadenza" class="ml-3 grey--text text--darken-1">Valida fino alle {{ scadenza }}</span>
					</div>
					<div class="grey--text text--darken-1 mb-2">
						Inquadra il QR code con la fotocamera del telefono. Ogni codice letto (EAN, UPC, Code128, QR…) e confermato con "Invia" entra qui come se fosse letto dalla pistola. Il collegamento resta attivo anche cambiando pagina nel magazzino.
					</div>
					<div v-if="url" class="mb-1">
						<a :href="url" target="_blank" rel="noopener" class="caption">{{ url }}</a>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-btn text color="primary" :loading="loading" @click="genera">Rigenera QR code</v-btn>
					<v-spacer />
					<v-btn depressed color="primary" @click="dialog = false">Chiudi</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import _ from 'lodash';

// La sessione vive in sessionStorage: cambiando pagina nel magazzino il telefono
// resta collegato senza dover rifare il QR code.
const STORAGE_KEY = 'azs.qrscan.session';
// Controllo periodico: se il telefono resta fermo oltre il TTL la sessione scade
// senza eventi, e il BO lo scopre solo interrogando il backend.
const CONTROLLO_MS = 2 * 60 * 1000;
// Dopo una riconnessione del socket si recuperano solo i codici recentissimi:
// un codice vecchio applicato in ritardo farebbe un carico/scarico inatteso.
const RECUPERO_MAX_MS = 30 * 1000;

const leggiStorage = () => {
	try {
		const raw = window.sessionStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : null;
	} catch (e) {
		return null;
	}
};

const scriviStorage = value => {
	try {
		if (value) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
		else window.sessionStorage.removeItem(STORAGE_KEY);
	} catch (e) {
		// storage non disponibile: la sessione vale solo per questa pagina
	}
};

export default {
	name: 'QrScanPanel',
	data() {
		return {
			loading: false,
			dialog: false,
			token: null,
			room: null,
			qr: null,
			url: null,
			absoluteDeadline: 0,
			collegato: false,
			scaduta: false,
			errore: '',
			lastSeq: 0,
			timer: null,
		};
	},
	computed: {
		attiva() {
			return !!this.token;
		},
		scadenza() {
			if (!this.absoluteDeadline) return '';
			const d = new Date(this.absoluteDeadline);
			return `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
		},
	},
	// Stesso canale di QrUploadPanel: l'opzione sockets del mixin vue-socket.io.
	sockets: {
		connect() {
			// dopo una riconnessione la room e' persa: senza rejoin il BO smette di ricevere
			if (!this.token) return;
			this.join();
			this.riconcilia(true);
		},
		QRSCAN_CONNECTED(raw) {
			const data = this.parse(raw);
			if (!data || data.token !== this.token) return;
			this.collegato = true;
			// telefono agganciato: il QR non serve piu'
			this.dialog = false;
		},
		QRSCAN_CODE(raw) {
			const data = this.parse(raw);
			if (!data || data.token !== this.token) return;
			this.collegato = true;
			this.ricevi(data.seq, data.code);
		},
		QRSCAN_CLOSED(raw) {
			const data = this.parse(raw);
			if (!data || data.token !== this.token) return;
			this.azzera(true);
		},
	},
	async mounted() {
		const salvata = leggiStorage();
		if (!salvata || !salvata.token) return;
		if (salvata.absoluteDeadline && Date.now() > salvata.absoluteDeadline) {
			scriviStorage(null);
			return;
		}
		this.token = salvata.token;
		this.room = salvata.room;
		this.qr = salvata.qr;
		this.url = salvata.url;
		this.absoluteDeadline = salvata.absoluteDeadline;
		this.join();
		await this.riconcilia(false);
		if (this.token) this.avviaControllo();
	},
	beforeDestroy() {
		// niente revoca: la sessione deve sopravvivere al cambio pagina
		this.fermaControllo();
	},
	methods: {
		async genera() {
			if (this.loading) return;
			this.loading = true;
			// una sessione per volta: la precedente viene revocata
			if (this.token) {
				const vecchio = this.token;
				this.azzera(false);
				await this.$store.dispatch('qrscan/revokeSession', vecchio);
			}
			this.scaduta = false;
			this.errore = '';
			this.dialog = true;

			const res = await this.$store.dispatch('qrscan/createSession');
			this.loading = false;
			if (!res || res.error || !res.token) {
				this.dialog = false;
				this.errore = 'Impossibile generare il QR code, riprova.';
				return;
			}

			this.token = res.token;
			this.room = res.room;
			this.qr = res.qr;
			this.url = res.url;
			this.absoluteDeadline = res.absoluteDeadline;
			this.collegato = false;
			this.lastSeq = 0;
			scriviStorage({ token: res.token, room: res.room, qr: res.qr, url: res.url, absoluteDeadline: res.absoluteDeadline });

			this.join();
			this.avviaControllo();
		},

		async disconnetti() {
			const token = this.token;
			this.azzera(false);
			if (token) await this.$store.dispatch('qrscan/revokeSession', token);
		},

		join() {
			if (!this.$socket || !this.room) return;
			this.$socket.emit('join', { room: this.room }, () => {});
		},

		// Riallinea lo stato con il backend. Al mount si parte dall'ultimo seq
		// (niente replay dei codici gia' gestiti da un'altra pagina); dopo una
		// riconnessione si recuperano solo i codici arrivati da pochi secondi.
		async riconcilia(recupera) {
			if (!this.token) return;
			const res = await this.$store.dispatch('qrscan/fetchSession', this.token);
			if (!res || res.error) {
				const codice = res && res.error && res.error.code;
				if (codice === 'session_expired' || codice === 'forbidden') this.azzera(true);
				return;
			}
			this.collegato = !!res.connected;
			if (recupera) {
				_.forEach(res.codes || [], c => {
					if (c.at && Date.now() - c.at <= RECUPERO_MAX_MS) this.ricevi(c.seq, c.code);
				});
			}
			this.lastSeq = Math.max(this.lastSeq, res.seq || 0);
		},

		ricevi(seq, code) {
			if (!code) return;
			// seq gia' visto: evento duplicato (riconnessione o doppio join)
			if (seq && seq <= this.lastSeq) return;
			if (seq) this.lastSeq = seq;
			this.$emit('code', String(code));
		},

		parse(raw) {
			if (!raw) return null;
			if (_.isObject(raw)) return raw;
			try {
				return JSON.parse(raw);
			} catch (e) {
				return null;
			}
		},

		avviaControllo() {
			this.fermaControllo();
			this.timer = setInterval(() => this.riconcilia(false), CONTROLLO_MS);
		},

		fermaControllo() {
			if (this.timer) clearInterval(this.timer);
			this.timer = null;
		},

		azzera(scaduta) {
			this.fermaControllo();
			scriviStorage(null);
			this.token = null;
			this.room = null;
			this.qr = null;
			this.url = null;
			this.absoluteDeadline = 0;
			this.collegato = false;
			this.lastSeq = 0;
			this.dialog = false;
			this.scaduta = scaduta;
		},
	},
};
</script>

<style scoped>
.qr-box {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 236px;
	height: 236px;
	background: #fff;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
}
</style>
